import { handleActions } from 'redux-actions'
import { fromJS } from 'immutable'
import moment from 'moment-timezone'
import { getDrilldownFilterData, isTimeline } from 'utils/chart'
import { isAiEntityKey } from 'utils/ai_entities'
import { rangeToDates } from 'utils/date'

import { FilterNameMapping, ChartTypes } from 'static/constants'

import * as AppActions from 'actions/app'
import * as Actions from 'actions/dashboard'

export const initialState = fromJS({
  additionalSavedSearchIds: [],
  chart: {},
  loading: false,
  show: false,
  dateFrom: null,
  dateTo: null,
  dateRange: null,
  dateType: null,
  searchParams: null,
  groupingType: 'channel',
  sortBy: '',
  comparisonPeriod: 'current',
  sortDirection: 'desc',
  filterData: null,
  secondFilter: null,
  savedSearchId: null
})

const getFilterKV = (filterName, filterData) => {
  if (filterData.get('id') === -1) {
    return { key: `exclude_${filterName}`, value: filterData.get('excludeIds') }
  }

  return { key: filterName, value: [filterData.get('id')] }
}

const addFilters = (searchParams, field, data, isSecondFilter) => {
  const result = { ...searchParams }
  const filterData = fromJS(data)

  let fieldName = field

  if (field === 'tonality') {
    fieldName = 'tonalities'
  } else if (field === 'sentiment') {
    fieldName = 'sentiments'
  } else if (isAiEntityKey(field)) {
    fieldName = 'aiEntities'
  }

  const newsFilterName = FilterNameMapping.news[fieldName]

  if (newsFilterName) {
    result.news = result.news || {}
    const { key, value } = getFilterKV(newsFilterName, filterData)
    result.news[key] = value
  }

  const publicationFilterName = FilterNameMapping.publication[fieldName]

  if (publicationFilterName) {
    result.publication = result.publication || {}
    const { key, value } = getFilterKV(publicationFilterName, filterData)
    result.publication[key] = value
  }

  const outlinkFilterName = FilterNameMapping.outlink[fieldName]

  if (outlinkFilterName) {
    result.outlink = result.outlink || {}
    const { key, value } = getFilterKV(outlinkFilterName, filterData)
    result.outlink[key] = value
  }

  if (fieldName === 'analysisCodes') {
    result.news = result.news || {}
    result.news.analysis = result.news.analysis || {}

    if (isSecondFilter) {
      const { key, value } = getFilterKV('grouped_code_ids', filterData)
      result.news.analysis[key] = value
    } else {
      const { key, value } = getFilterKV('selected_code_ids', filterData)
      result.news.analysis[key] = value
    }
  }

  if (fieldName === 'influencers') {
    result.news = result.news || { identity_id: [] }
    result.news.identity_id = result.news.identity_id.concat(filterData.get('identityIds').toJS())
  }

  if (fieldName === 'statementTonalities') {
    result.news = result.news || {}
    result.news.analysis = result.news.analysis || {}
    const { key, value } = getFilterKV('statement_tonalities', filterData)
    result.news.analysis[key] = value
  }

  return result
}

const buildSearchParams = (chart, filterData, secondFilter) => {
  let searchParams = {}

  if (filterData && Object.keys(filterData).length > 0) {
    searchParams = addFilters(searchParams, chart.get('firstLevel'), filterData)
  }

  if (secondFilter) {
    searchParams = addFilters(searchParams, chart.get('secondLevel'), secondFilter, true)
  }

  return fromJS(searchParams)
}

const usesPreviousComparisonPeriod = (chart, filterData, secondFilter) => (
  chart.getIn(['opts', 'showPreviousYear'])
  && [filterData, secondFilter].some(filter => filter && filter.get('comparisonPeriod') === 'previous')
)

const shiftPreviousYearDates = (dateFrom, dateTo, dateRange) => {
  if (dateFrom || dateTo) {
    return {
      dateFrom: dateFrom ? moment(dateFrom).subtract(1, 'year').toISOString() : null,
      dateTo: dateTo ? moment(dateTo).subtract(1, 'year').toISOString() : null,
      dateRange: null
    }
  }

  if (dateRange) {
    const { from, to } = rangeToDates(dateRange)

    return {
      dateFrom: from ? moment(from).subtract(1, 'year').toISOString() : null,
      dateTo: to ? moment(to).subtract(1, 'year').toISOString() : null,
      dateRange: null
    }
  }

  return { dateFrom, dateTo, dateRange }
}

const shiftRangeDates = (range, amount) => ({
  from: range.get('from') ? moment(range.get('from')).add(amount, 'year').toISOString() : null,
  to: range.get('to') ? moment(range.get('to')).add(amount, 'year').toISOString() : null
})

const rangeForComparisonPeriod = (filterData, comparisonPeriod) => {
  if (!(filterData && filterData.get && filterData.get('range'))) {
    return null
  }

  const sourceComparisonPeriod = filterData.get('comparisonPeriod') || 'current'
  const range = filterData.get('range')

  if (sourceComparisonPeriod === comparisonPeriod) {
    return {
      from: range.get('from'),
      to: range.get('to')
    }
  }

  if (sourceComparisonPeriod === 'current' && comparisonPeriod === 'previous') {
    return shiftRangeDates(range, -1)
  }

  if (sourceComparisonPeriod === 'previous' && comparisonPeriod === 'current') {
    return shiftRangeDates(range, 1)
  }

  return {
    from: range.get('from'),
    to: range.get('to')
  }
}

const buildDrilldownState = (chart, filterData, secondFilter, comparisonPeriod) => {
  let searchParams = null
  let dateFrom = chart.get('dateFrom')
  let dateTo = chart.get('dateTo')
  let dateRange = chart.get('dateRange')
  let savedSearchId = null
  const dateType = chart.get('dateType') || chart.getIn(['savedSearch', 'dateType'])
  const shouldUsePreviousYearDates = comparisonPeriod === 'previous'

  if (!isTimeline(chart) || chart.get('type') === ChartTypes.GAUGE) {
    searchParams = buildSearchParams(chart, filterData && filterData.toJS ? filterData.toJS() : filterData, secondFilter)
  } else {
    const comparisonRange = rangeForComparisonPeriod(filterData, comparisonPeriod)

    if (comparisonRange) {
      dateFrom = comparisonRange.from
      dateTo = comparisonRange.to
      dateRange = null
    }

    searchParams = buildSearchParams(chart, null, secondFilter)
  }

  if (shouldUsePreviousYearDates && !(filterData && filterData.get && filterData.get('range'))) {
    const shiftedDates = shiftPreviousYearDates(dateFrom, dateTo, dateRange)

    dateFrom = shiftedDates.dateFrom
    dateTo = shiftedDates.dateTo
    dateRange = shiftedDates.dateRange
  }

  let additionalSavedSearchIds = fromJS([])

  if (!secondFilter && chart.get('secondLevel') === 'savedSearches') {
    additionalSavedSearchIds = chart.get('additionalSavedSearchIds') || []
  }

  if (chart.get('firstLevel') === 'savedSearches' && filterData) {
    savedSearchId = filterData.get('id')
  } else if (chart.get('secondLevel') === 'savedSearches' && secondFilter) {
    savedSearchId = secondFilter.get('id')
  } else {
    savedSearchId = chart.getIn(['savedSearch', 'id'])
  }

  return {
    additionalSavedSearchIds,
    chart,
    dateFrom,
    dateTo,
    dateRange,
    dateType,
    savedSearchId,
    groupingType: chart.getIn(['savedSearch', 'groupingType']),
    sortBy: chart.get('sortBy') || chart.getIn(['savedSearch', 'sortBy']),
    sortDirection: chart.get('sortDirection') || chart.getIn(['savedSearch', 'sortDirection']) || 'desc',
    comparisonPeriod,
    searchParams,
    filterData: filterData ? filterData.delete('searchParams') : null,
    secondFilter
  }
}

export default handleActions({
  [Actions.showDrilldownDialog]: (state, { payload: { chart, d, secondFilter: sF } }) => {
    const filterData = getDrilldownFilterData(d)
    const immutableFilterData = filterData ? fromJS(filterData) : null
    const secondFilter = sF ? fromJS(sF) : null
    const comparisonPeriod = usesPreviousComparisonPeriod(chart, immutableFilterData, secondFilter) ? 'previous' : 'current'

    return state.merge({
      show: true,
      ...buildDrilldownState(chart, immutableFilterData, secondFilter, comparisonPeriod)
    })
  },
  [Actions.hideDrilldownDialog]: state => state.set('show', false),

  [Actions.loadDrilldownNewsFeedNewsStart]: state => state.set('loading', true),
  [Actions.loadDrilldownNewsFeedNewsError]: state => state.set('loading', false),
  [Actions.loadDrilldownNewsFeedNewsSuccess]: state => state.set('loading', false),

  [Actions.selectDrilldownGroupingType]: (state, { payload: groupingType }) => state.set('groupingType', groupingType),
  [Actions.selectDrilldownSortBy]: (state, { payload: sortBy }) => state.set('sortBy', sortBy),
  [Actions.selectDrilldownComparisonPeriod]: (state, { payload: comparisonPeriod }) => (
    state.merge(buildDrilldownState(state.get('chart'), state.get('filterData'), state.get('secondFilter'), comparisonPeriod))
  ),
  [Actions.selectDrilldownSortDirection]: (state, { payload: sortDirection }) => state.set('sortDirection', sortDirection),

  [AppActions.resetState]: () => initialState
}, initialState)
